import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import {
  CheckSquare,
  Eye,
  MoreHorizontal,
  Square,
  SquareMinus,
  Trash2,
} from "lucide-react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { cn } from "@/shared/lib/cn";
import {
  resolveActionIcon,
  resolveActionLabel,
} from "@/shared/lib/actionIcons";
import { useMediaQuery } from "@/shared/lib/useMediaQuery";
import { LoadMoreSentinel } from "@/shared/list/LoadMoreSentinel";
import { EmptyState } from "@/shared/ui/core/EmptyState";
import { ErrorState } from "@/shared/ui/core/ErrorState";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/core/DropdownMenu";
import {
  LoadingOverlay,
  IndeterminateProgressBar,
} from "@/shared/ui/core/Loading";

export type DataTableColumn<T> = {
  id: string;
  header: string;
  cell: (row: T) => ReactNode;
  align?: "left" | "right" | "center";
  dataType?: "text" | "number" | "date" | "status" | "center";
  mono?: boolean;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  /** If true, truncate cell text with ellipsis when maxWidth is exceeded. Default true when maxWidth is set. */
  truncate?: boolean;
  /** When false, column is omitted from mobile card body (desktop unchanged). Default true. */
  hideOnMobile?: boolean;
};

/** Honor `\n` in column headers (e.g. "Hạn mức tín dụng\\nđề xuất"). */
function headerIsMultiline(header: string): boolean {
  return header.includes("\n");
}

function HeaderLabel({
  header,
  truncate = false,
}: {
  header: string;
  truncate?: boolean;
}) {
  const multiline = headerIsMultiline(header);
  return (
    <span
      className={cn(
        multiline
          ? "whitespace-pre-line leading-tight normal-case"
          : "whitespace-nowrap",
        truncate && "truncate block",
      )}
    >
      {header}
    </span>
  );
}

/**
 * Standard column alignment resolver:
 * - Thao tác, #, Trạng thái, Ngày giờ -> Center
 * - Number (SL, tiền, tỷ lệ, v.v.) -> Right
 * - Text (Mã, tên, mô tả, khách hàng, v.v.) -> Left
 */
export function resolveColumnAlignment(col: {
  id: string;
  header?: string;
  align?: "left" | "right" | "center";
  dataType?: "text" | "number" | "date" | "status" | "center";
}): "left" | "right" | "center" {
  if (col.align) return col.align;

  if (col.dataType) {
    switch (col.dataType) {
      case "number":
        return "right";
      case "date":
      case "status":
      case "center":
        return "center";
      case "text":
      default:
        return "left";
    }
  }

  const id = col.id.toLowerCase();
  const header = (col.header ?? "").toLowerCase();

  // 1. Thao tác / Action / Nút -> Center
  if (
    id === "action" ||
    id === "actions" ||
    id.includes("action") ||
    id.includes("btn") ||
    header.includes("thao tác")
  ) {
    return "center";
  }

  // 2. STT / # -> Center
  if (
    id === "#" ||
    id === "seq" ||
    id === "index" ||
    id === "stt" ||
    header === "#" ||
    header === "stt"
  ) {
    return "center";
  }

  // 3. Trạng thái -> Center
  if (
    id === "status" ||
    id.includes("status") ||
    id === "state" ||
    id === "st" ||
    id === "qc" ||
    id === "inv" ||
    id === "inventory" ||
    header.includes("trạng thái") ||
    header.includes("tình trạng") ||
    header === "tt" ||
    header === "qc" ||
    header === "tồn"
  ) {
    return "center";
  }

  // 4. Ngày giờ -> Center
  if (
    id.includes("date") ||
    id.includes("time") ||
    id.endsWith("at") ||
    id === "due" ||
    id.includes("sla") ||
    header.includes("ngày") ||
    header.includes("giờ") ||
    header.includes("thời gian") ||
    header.includes("hạn giao") ||
    header.includes("hạn dùng") ||
    header.includes("hạn") ||
    header.includes("date") ||
    header.includes("time") ||
    header.includes("sla")
  ) {
    return "center";
  }

  // 5. Number -> Right
  if (
    id === "qty" ||
    id.includes("qty") ||
    id.includes("quantity") ||
    id.includes("amount") ||
    id.includes("price") ||
    id.includes("total") ||
    id.includes("count") ||
    id.includes("pct") ||
    id.includes("percent") ||
    id.includes("rate") ||
    id.includes("cost") ||
    id === "need" ||
    id === "reserved" ||
    id === "still" ||
    id === "balance" ||
    header.includes("số lượng") ||
    header.includes("sl ") ||
    header.startsWith("sl") ||
    header.includes("đơn giá") ||
    (header.includes("giá") && !header.includes("đánh giá")) ||
    header.includes("thành tiền") ||
    header.includes("tổng cộng") ||
    header.includes("tổng") ||
    header.includes("tỷ lệ") ||
    header.includes("%") ||
    header.includes("đã giữ") ||
    header.includes("còn cần") ||
    header.includes("cần (base)")
  ) {
    return "right";
  }

  // 6. Text mặc định -> Left
  return "left";
}

export type DataTableRowAction = {
  id: string;
  label: string;
  /** Explicit custom icon element to render. */
  icon?: ReactNode;
  /** Icon lookup when `id` is unique per row and does not match a shared action key. */
  iconKey?: string;
  onClick?: () => void;
  to?: string;
  disabled?: boolean;
  /** Destructive action styling. */
  danger?: boolean;
};

export type DataTableLoadMore = {
  hasMore: boolean;
  onLoadMore: () => void;
  shown: number;
  total: number;
  loading?: boolean;
};

export type DataTableProps<T> = {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  /**
   * Header appearance variant:
   * - 'primary': Master list tables (green sticky header with text-on-primary).
   * - 'subtle': Detail page tables / sub-tables (neutral bg-bg/80 header with text-text-secondary, matching TestUI).
   * Default: 'primary'.
   */
  variant?: "primary" | "subtle";
  dense?: boolean;
  /** Alternating row background colors (zebra striping). Default true. */
  striped?: boolean;
  /**
   * Override table min-width utility (default: min-w-[640px]).
   * Useful for compact modals where horizontal scroll hurts UX.
   */
  minWidthClassName?: string;
  loading?: boolean;
  /**
   * Explicit indicator for page, filter, or sort transitions with existing rows.
   * Keeps the table structure mounted and shows a sleek overlay and top progress bar.
   */
  pageLoading?: boolean;
  /** Custom label for page loading overlay. Default: "Đang tải trang..." */
  loadingText?: string;
  error?: string | null;
  emptyTitle?: string;
  emptyDescription?: string;
  /** Icon inside the empty-state circle. Defaults to Inbox. */
  emptyIcon?: ReactNode;
  /** Desktop: keep `#` / Thao tác / column headers when `rows` is empty. */
  emptyKeepHeader?: boolean;
  onRetry?: () => void;
  onRowClick?: (row: T) => void;
  sortKey?: string;
  sortDir?: "asc" | "desc";
  onSortChange?: (columnId: string) => void;
  /**
   * 1-based absolute index of the first visible row (for paginated lists).
   * Example: page 2, pageSize 20 → rowNumberStart={21}.
   */
  rowNumberStart?: number;
  /**
   * Show row number # column. Default true.
   * Can be disabled for detail tables that don't need row index sequence.
   */
  showRowNumbers?: boolean;
  /**
   * Show Thao tác column immediately after # (prototype parity).
   * Default true for Ops/entity lists; requires `getRowActions` or the column is hidden
   * (avoids empty ⋯ menus).
   */
  showRowActions?: boolean;
  /**
   * Per-row menu items for the Thao tác column.
   * Empty array → trigger disabled (column still visible when showRowActions).
   */
  getRowActions?: (row: T) => DataTableRowAction[];
  /** Desktop checkbox column (TestUiListTable). */
  selectable?: boolean;
  selectedIds?: string[];
  onToggleSelectAll?: () => void;
  onToggleSelectRow?: (id: string, e: MouseEvent) => void;
  /** Mobile card list: infinite scroll / load-more footer. */
  loadMore?: DataTableLoadMore;
  /**
   * Force mobile card view regardless of viewport width.
   * Useful when rendered inside mobile shells (FieldShell) on large displays.
   */
  forceMobileView?: boolean;
  /**
   * Custom mobile card body. When set, replaces the default title + dl column dump.
   * Row chrome (#, click, load-more) stays in DataTable.
   */
  renderMobileCard?: (row: T) => ReactNode;
  /** Optional custom class for the row actions dropdown menu content (e.g. min-w/max-w overrides). */
  rowActionsMenuClassName?: string;
  /** Desktop `<tfoot>` (or footer rows) rendered inside the table. */
  tableFooter?: ReactNode;
  /** Summary shown under the mobile card list. */
  mobileFooter?: ReactNode;
  className?: string;
};

const actionTriggerClass =
  "inline-flex size-7 items-center justify-center rounded-control bg-transparent text-text-secondary hover:text-primary transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)] disabled:pointer-events-none disabled:opacity-50";

function RowActionsMenu({
  actions,
  menuClassName,
  onActionTrigger,
}: {
  actions: DataTableRowAction[];
  dense?: boolean;
  menuClassName?: string;
  onActionTrigger?: () => void;
}) {
  const disabled = actions.length === 0;
  const firstDangerIndex = actions.findIndex((a) => a.danger);

  if (disabled) {
    return (
      <button
        type="button"
        aria-label="Thao tác"
        disabled
        className={actionTriggerClass}
        onClick={(e) => {
          e.stopPropagation();
          onActionTrigger?.();
        }}
        onPointerDown={(e) => {
          e.stopPropagation();
          onActionTrigger?.();
        }}
      >
        <MoreHorizontal className="size-4 pointer-events-none" />
      </button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Thao tác"
          className={actionTriggerClass}
          onClick={(e) => {
            e.stopPropagation();
            onActionTrigger?.();
          }}
          onPointerDown={(e) => {
            e.stopPropagation();
            onActionTrigger?.();
          }}
        >
          <MoreHorizontal className="size-4 pointer-events-none" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={-20}
        className={cn(
          "w-auto min-w-[11.5rem] max-w-[20rem] rounded-xl border border-border/80 bg-surface p-1.5 shadow-xl overflow-visible",
          menuClassName,
        )}
        onClick={(e) => {
          e.stopPropagation();
          onActionTrigger?.();
        }}
      >
        <DropdownMenuPrimitive.Arrow
          asChild
          width={12}
          height={6}
          className="-mt-[1px]"
        >
          <svg
            width={12}
            height={6}
            viewBox="0 0 30 10"
            preserveAspectRatio="none"
            className="block overflow-visible"
          >
            <path
              d="M 0 0 L 15 10 L 30 0"
              className="fill-surface stroke-border stroke-[1.25]"
            />
          </svg>
        </DropdownMenuPrimitive.Arrow>
        {actions.map((action, index) => {
          const resolvedIcon = action.icon ? (
            <span
              className={cn(
                "size-4 shrink-0 flex items-center justify-center [&>svg]:size-4",
                action.danger ? "text-danger-fg" : "text-primary",
              )}
            >
              {action.icon}
            </span>
          ) : (
            resolveActionIcon(
              action.id,
              action.iconKey,
              action.danger
                ? "size-4 shrink-0 text-danger-fg"
                : "size-4 shrink-0 text-primary",
              action.label,
              { fallback: true, defaultIcon: action.danger ? Trash2 : Eye },
            )
          );
          const label =
            action.label || resolveActionLabel(action.id, action.label);
          const itemClass = cn(
            "cursor-pointer gap-2.5 px-3 py-2 text-caption font-medium rounded-lg transition-colors whitespace-nowrap",
            "hover:bg-primary-soft/60 focus:bg-primary-soft/60 data-[highlighted]:bg-primary-soft/60 text-text",
            action.danger &&
              "text-danger-fg focus:bg-danger-soft focus:text-danger-fg hover:bg-danger-soft hover:text-danger-fg data-[highlighted]:bg-danger-soft data-[highlighted]:text-danger-fg",
          );
          const item =
            action.to && !action.disabled ? (
              <DropdownMenuItem
                asChild
                className={itemClass}
                onPointerDown={() => onActionTrigger?.()}
                onClick={() => onActionTrigger?.()}
              >
                <Link
                  to={action.to}
                  aria-label={action.label}
                  className="flex items-center gap-2.5 w-full whitespace-nowrap text-inherit no-underline"
                  onPointerDown={() => onActionTrigger?.()}
                  onClick={() => onActionTrigger?.()}
                >
                  {resolvedIcon}
                  <span className="whitespace-nowrap">{label}</span>
                </Link>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                disabled={action.disabled}
                className={itemClass}
                onPointerDown={() => onActionTrigger?.()}
                onPointerUp={() => onActionTrigger?.()}
                onClick={() => onActionTrigger?.()}
                onSelect={() => {
                  onActionTrigger?.();
                  if (action.disabled) return;
                  action.onClick?.();
                }}
              >
                {resolvedIcon}
                <span className="whitespace-nowrap">{label}</span>
              </DropdownMenuItem>
            );
          return (
            <Fragment key={action.id}>
              {firstDangerIndex === index && firstDangerIndex > 0 ? (
                <DropdownMenuSeparator className="-mx-1 my-1 h-px bg-border/60" />
              ) : null}
              {item}
            </Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function DataTable<T>({
  columns,
  rows,
  getRowId,
  variant = "primary",
  dense = false,
  striped = true,
  minWidthClassName = "min-w-[640px]",
  loading = false,
  pageLoading = false,
  loadingText = "Đang tải trang…",
  error = null,
  emptyTitle = "Không có dữ liệu",
  emptyDescription = "Thử đổi bộ lọc hoặc tạo bản ghi mới.",
  emptyIcon,
  emptyKeepHeader = false,
  onRetry,
  onRowClick,
  sortKey,
  sortDir,
  onSortChange,
  rowNumberStart = 1,
  showRowNumbers = true,
  showRowActions = true,
  getRowActions,
  selectable = false,
  selectedIds,
  onToggleSelectAll,
  onToggleSelectRow,
  loadMore,
  forceMobileView = false,
  renderMobileCard,
  rowActionsMenuClassName,
  tableFooter,
  mobileFooter,
  className,
}: DataTableProps<T>) {
  /** Avoid empty ⋯ menus: column only when a getter exists. */
  const showActions = showRowActions && typeof getRowActions === "function";
  const isSubtle = variant === "subtle";
  const selected = selectedIds ?? [];
  const allSelected =
    selectable && rows.length > 0 && selected.length === rows.length;
  const isIndeterminate =
    selectable && selected.length > 0 && selected.length < rows.length;
  const leadingColCount =
    (selectable ? 1 : 0) + (showRowNumbers ? 1 : 0) + (showActions ? 1 : 0);
  const mediaIsDesktop = useMediaQuery("(min-width: 1024px)");
  const isDesktop = forceMobileView ? false : mediaIsDesktop;

  const lastActionClickTimeRef = useRef(0);
  const markActionTriggered = useCallback(() => {
    lastActionClickTimeRef.current = Date.now();
  }, []);

  const isInitialLoading = Boolean(loading && rows.length === 0);
  const isPageLoading = Boolean(pageLoading || (loading && rows.length > 0));

  // Only show the prominent center LoadingOverlay if the fetch takes longer than 350ms.
  // Fast filter changes (< 350ms) rely purely on the top IndeterminateProgressBar and row dimming,
  // completely eliminating flashing modals / jitter.
  const [showSlowOverlay, setShowSlowOverlay] = useState(false);

  useEffect(() => {
    if (!isPageLoading) {
      setShowSlowOverlay(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setShowSlowOverlay(true);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [isPageLoading]);

  if (error) {
    return (
      <div
        className={cn(
          "rounded-card border border-border bg-surface p-6",
          className,
        )}
      >
        <ErrorState
          title="Không tải được bảng"
          description={error}
          onRetry={onRetry}
        />
      </div>
    );
  }

  if (rows.length === 0 && !emptyKeepHeader && !isInitialLoading) {
    return (
      <div
        className={cn(
          "rounded-card border border-border bg-surface p-6",
          className,
        )}
      >
        <EmptyState
          title={emptyTitle}
          description={emptyDescription}
          icon={emptyIcon}
        />
      </div>
    );
  }

  const cellPad = dense ? "px-3 py-2" : "px-3 py-2.5";
  const indexPad = "px-3 py-2.5";
  const bodyPad = dense ? "px-3 py-2" : "px-3 py-2";

  if (!isDesktop) {
    if (isInitialLoading) {
      return (
        <div className={cn("space-y-2", className)} aria-busy>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="rounded-card border border-border bg-surface p-3 shadow-1 space-y-2.5 animate-pulse"
            >
              <div className="h-3 w-10 bg-border/60 rounded-xs" />
              <div className="h-4 w-1/2 bg-border/80 rounded-xs" />
              <div className="space-y-1.5 border-t border-border-subtle pt-2">
                <div className="flex justify-between">
                  <div className="h-3 w-16 bg-border/50 rounded-xs" />
                  <div className="h-3 w-24 bg-border/60 rounded-xs" />
                </div>
                <div className="flex justify-between">
                  <div className="h-3 w-16 bg-border/50 rounded-xs" />
                  <div className="h-3 w-20 bg-border/60 rounded-xs" />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }
    if (rows.length === 0) {
      return (
        <div
          className={cn(
            "rounded-card border border-border bg-surface p-6",
            className,
          )}
        >
          <EmptyState
            title={emptyTitle}
            description={emptyDescription}
            icon={emptyIcon}
          />
        </div>
      );
    }
    const mobileCols = columns.filter((c) => !c.hideOnMobile);
    const [titleCol, ...restCols] = mobileCols;
    return (
      <div className={cn("relative space-y-2", className)}>
        {isPageLoading ? (
          <IndeterminateProgressBar className="sticky top-0 left-0 right-0 z-30 rounded-full" />
        ) : null}
        <LoadingOverlay
          visible={showSlowOverlay}
          label={loadingText}
          className="z-20 rounded-card"
        />
        <ul
          className={cn(
            "m-0 list-none space-y-2 p-0 transition-opacity duration-200",
            // Dim only — keep taps alive during soft refetch (Android WebView).
            isPageLoading && "opacity-60",
          )}
        >
          {rows.map((row, rowIndex) => {
            const id = getRowId(row);
            return (
              <li key={id} className="relative">
                <div
                  data-row-card=""
                  className={cn(
                    "rounded-card border border-border bg-surface p-3 shadow-1 select-none",
                    onRowClick &&
                      // Nested links must not steal the tap; the overlay <button> opens the row.
                      "[&_a]:pointer-events-none",
                  )}
                >
                  <p className="m-0 font-mono text-[11px] tabular-nums text-text-muted">
                    #{rowNumberStart + rowIndex}
                  </p>
                  {renderMobileCard ? (
                    <div className="mt-0.5">{renderMobileCard(row)}</div>
                  ) : (
                    <>
                      {titleCol ? (
                        <div
                          className={cn(
                            "mt-0.5 text-body font-semibold text-text",
                            titleCol.mono && "font-mono",
                          )}
                        >
                          {titleCol.cell(row)}
                        </div>
                      ) : null}
                      {restCols.length > 0 ? (
                        <dl className="m-0 mt-2 space-y-1.5 border-t border-border-subtle pt-2">
                          {restCols.map((col) => (
                            <div
                              key={col.id}
                              className="flex items-start justify-between gap-3 text-caption"
                            >
                              <dt className="shrink-0 text-text-muted">
                                <HeaderLabel header={col.header} />
                              </dt>
                              <dd
                                className={cn(
                                  "m-0 min-w-0 text-right text-text",
                                  col.mono && "font-mono tabular-nums",
                                )}
                              >
                                {col.cell(row)}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      ) : null}
                    </>
                  )}
                </div>
                {onRowClick ? (
                  <button
                    type="button"
                    data-row-card-hit=""
                    aria-label="Xem chi tiết"
                    className={cn(
                      "absolute inset-0 z-[1] rounded-card border-0 bg-transparent p-0",
                      "cursor-pointer touch-manipulation",
                      "hover:bg-primary-soft/30 active:bg-primary-soft/50",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]",
                    )}
                    onClick={(e) => {
                      e.preventDefault();
                      if (Date.now() - lastActionClickTimeRef.current < 450)
                        return;
                      window.getSelection()?.removeAllRanges();
                      onRowClick(row);
                    }}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
        {loadMore ? (
          <LoadMoreSentinel
            hasMore={loadMore.hasMore}
            onLoadMore={loadMore.onLoadMore}
            shown={loadMore.shown}
            total={loadMore.total}
            loading={loadMore.loading}
          />
        ) : null}
        {mobileFooter}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative w-full min-w-0 max-w-full overflow-hidden rounded-control border bg-surface min-h-[260px]",
        isSubtle ? "border-border/70 shadow-none" : "border-border shadow-xs",
        className,
      )}
    >
      {isPageLoading ? (
        <IndeterminateProgressBar className="absolute top-0 left-0 right-0 z-30" />
      ) : null}
      <LoadingOverlay
        visible={showSlowOverlay}
        label={loadingText}
        className="z-20"
      />
      <div className="min-w-full overflow-x-auto overscroll-x-contain scrollbar-thin">
        <table
          className={cn(
            "w-full min-w-full border-collapse text-left text-body",
            minWidthClassName,
          )}
        >
          <thead
            className={cn(
              "sticky top-0 select-none text-[11px] font-semibold tracking-wider uppercase",
              isSubtle
                ? "border-b border-border bg-bg/80 text-text-secondary"
                : "table-head-primary bg-primary text-on-primary",
            )}
          >
            <tr
              className={
                isSubtle
                  ? "border-b border-border"
                  : "border-b border-primary-hover"
              }
            >
              {selectable ? (
                <th
                  scope="col"
                  className={cn(
                    "w-10 px-3 py-2.5 text-center align-middle",
                    isSubtle ? "text-text-secondary" : "text-on-primary",
                  )}
                >
                  <button
                    type="button"
                    onClick={onToggleSelectAll}
                    className={cn(
                      "inline-flex items-center justify-center align-middle leading-none cursor-pointer",
                      isSubtle
                        ? "text-text-muted hover:text-text"
                        : "text-on-primary/90 hover:text-on-primary",
                    )}
                    aria-label="Chọn tất cả"
                  >
                    {allSelected ? (
                      <CheckSquare className="size-4" />
                    ) : isIndeterminate ? (
                      <SquareMinus
                        className={cn("size-4", isSubtle && "text-primary")}
                      />
                    ) : (
                      <Square className="size-4 opacity-70 hover:opacity-100" />
                    )}
                  </button>
                </th>
              ) : null}
              {showRowNumbers ? (
                <th
                  scope="col"
                  style={{ width: "1%", maxWidth: "4.25rem" }}
                  className={cn(
                    "w-px px-1.5 py-2.5 text-center text-caption font-semibold tracking-wider uppercase whitespace-nowrap",
                    isSubtle ? "text-text-secondary" : "text-on-primary",
                  )}
                >
                  #
                </th>
              ) : null}
              {showActions ? (
                <th
                  scope="col"
                  className={cn(
                    indexPad,
                    "w-20 whitespace-nowrap text-center text-caption font-semibold tracking-wider uppercase",
                    isSubtle ? "text-text-secondary" : "text-on-primary",
                  )}
                >
                  Thao tác
                </th>
              ) : null}
              {columns.map((col) => {
                const align = resolveColumnAlignment(col);
                const sortable = Boolean(onSortChange);
                const isSorted = sortKey === col.id;
                const ariaSort = !sortable
                  ? undefined
                  : isSorted
                    ? sortDir === "desc"
                      ? "descending"
                      : "ascending"
                    : "none";
                const colStyle = {
                  width: col.width,
                  minWidth: col.minWidth,
                  maxWidth: col.maxWidth,
                };
                const shouldTruncate = col.truncate ?? Boolean(col.maxWidth);
                const multiline = headerIsMultiline(col.header);
                return (
                  <th
                    key={col.id}
                    scope="col"
                    aria-sort={ariaSort}
                    style={colStyle}
                    className={cn(
                      cellPad,
                      "text-caption font-semibold tracking-wider uppercase align-bottom",
                      multiline ? "whitespace-normal" : "whitespace-nowrap",
                      shouldTruncate && "overflow-hidden text-ellipsis",
                      isSubtle ? "text-text-secondary" : "text-on-primary",
                      align === "left" && "text-left",
                      align === "center" && "text-center",
                      align === "right" && "text-right",
                    )}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        className={cn(
                          "inline-flex items-center gap-1 rounded-control focus-visible:outline-none focus-visible:ring-2 max-w-full",
                          multiline
                            ? "items-end whitespace-normal"
                            : "whitespace-nowrap",
                          align === "left" && "justify-start",
                          align === "center" && "justify-center",
                          align === "right" && "justify-end w-full",
                          isSubtle
                            ? "text-inherit focus-visible:ring-[var(--color-focus-ring)]"
                            : "text-inherit focus-visible:ring-[var(--color-on-primary)]",
                        )}
                        onClick={() => onSortChange?.(col.id)}
                        aria-label={
                          isSorted
                            ? `${col.header.replace(/\n/g, " ")}, sắp xếp ${sortDir === "desc" ? "giảm dần" : "tăng dần"}`
                            : `${col.header.replace(/\n/g, " ")}, sắp xếp`
                        }
                      >
                        <HeaderLabel
                          header={col.header}
                          truncate={shouldTruncate && !multiline}
                        />
                        {isSorted ? (
                          <span
                            aria-hidden
                            className={cn(
                              "shrink-0",
                              isSubtle ? "text-primary" : "text-accent",
                            )}
                          >
                            {sortDir === "desc" ? "↓" : "↑"}
                          </span>
                        ) : null}
                      </button>
                    ) : (
                      <HeaderLabel
                        header={col.header}
                        truncate={shouldTruncate && !multiline}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody
            className={cn(
              "divide-y divide-border/60 transition-opacity duration-200",
              isPageLoading && "opacity-60 pointer-events-none",
            )}
          >
            {isInitialLoading ? (
              Array.from({ length: 5 }, (_, rIdx) => (
                <tr key={rIdx} className="bg-surface animate-pulse">
                  {selectable ? (
                    <td className="w-10 px-3 py-3 text-center">
                      <div className="size-4 mx-auto rounded-xs bg-border/60" />
                    </td>
                  ) : null}
                  {showRowNumbers ? (
                    <td
                      style={{ width: "1%", maxWidth: "4.25rem" }}
                      className="w-px px-1.5 py-3 text-center"
                    >
                      <div className="mx-auto h-3.5 w-2.5 rounded-xs bg-border/60" />
                    </td>
                  ) : null}
                  {showActions ? (
                    <td className="w-20 px-3 py-3 text-center">
                      <div className="size-6 mx-auto rounded-control bg-border/50" />
                    </td>
                  ) : null}
                  {columns.map((col) => (
                    <td key={col.id} className={cn(cellPad)}>
                      <div
                        className={cn(
                          "h-3.5 rounded-xs bg-border/60",
                          col.width === "1%" ? "w-14" : "w-3/4 max-w-[200px]",
                          col.align === "right"
                            ? "ml-auto"
                            : col.align === "center"
                              ? "mx-auto"
                              : "",
                        )}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={leadingColCount + columns.length} className="p-0">
                  <EmptyState
                    title={emptyTitle}
                    description={emptyDescription}
                    icon={emptyIcon}
                  />
                </td>
              </tr>
            ) : null}
            {rows.map((row, rowIndex) => {
              const id = getRowId(row);
              const actions = showActions ? (getRowActions?.(row) ?? []) : [];
              const isSelected = selectable && selected.includes(id);
              return (
                <tr
                  key={id}
                  className={cn(
                    "group transition-colors",
                    isSelected
                      ? "bg-primary-soft"
                      : striped && rowIndex % 2 === 1
                        ? "bg-table-stripe"
                        : "bg-surface",
                    "hover:bg-primary-soft/60",
                    onRowClick && "cursor-pointer",
                  )}
                  onClick={
                    onRowClick
                      ? (e) => {
                          if (Date.now() - lastActionClickTimeRef.current < 450)
                            return;
                          const target = e.target as HTMLElement | null;
                          const interactive = target?.closest(
                            'button, a, input, select, textarea, [role="button"], [role="menuitem"], [data-radix-dropdown-menu-content]',
                          );
                          if (interactive && interactive !== e.currentTarget) {
                            return;
                          }
                          // Khi người dùng quét / bôi đen text để copy, không kích hoạt onRowClick
                          const selection = window.getSelection();
                          if (
                            selection &&
                            selection.toString().trim().length > 0
                          ) {
                            return;
                          }
                          onRowClick(row);
                        }
                      : undefined
                  }
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                  tabIndex={onRowClick ? 0 : undefined}
                >
                  {selectable ? (
                    <td
                      className={cn(bodyPad, "text-center align-middle")}
                      onClick={(e) => onToggleSelectRow?.(id, e)}
                    >
                      <button
                        type="button"
                        className="inline-flex items-center justify-center align-middle leading-none text-text-muted group-hover:text-primary cursor-pointer"
                        aria-label={`Chọn dòng ${rowNumberStart + rowIndex}`}
                      >
                        {isSelected ? (
                          <CheckSquare className="size-4 text-primary" />
                        ) : (
                          <Square className="size-4 opacity-50 hover:opacity-100" />
                        )}
                      </button>
                    </td>
                  ) : null}
                  {showRowNumbers ? (
                    <td
                      style={{ width: "3%", maxWidth: "4.25rem" }}
                      className="w-px px-1.5 py-2 text-center font-mono text-caption tabular-nums text-text-muted"
                    >
                      {rowNumberStart + rowIndex}
                    </td>
                  ) : null}
                  {showActions ? (
                    <td
                      className={cn(bodyPad, "text-center whitespace-nowrap")}
                      onClick={(e) => {
                        e.stopPropagation();
                        markActionTriggered();
                      }}
                    >
                      <RowActionsMenu
                        actions={actions}
                        dense={dense}
                        menuClassName={rowActionsMenuClassName}
                        onActionTrigger={markActionTriggered}
                      />
                    </td>
                  ) : null}
                  {columns.map((col) => {
                    const align = resolveColumnAlignment(col);
                    const colStyle = {
                      width: col.width,
                      minWidth: col.minWidth,
                      maxWidth: col.maxWidth,
                    };
                    const shouldTruncate =
                      col.truncate ?? Boolean(col.maxWidth);
                    return (
                      <td
                        key={col.id}
                        style={colStyle}
                        className={cn(
                          bodyPad,
                          "whitespace-nowrap text-text",
                          shouldTruncate && "overflow-hidden text-ellipsis",
                          col.mono && "font-mono tabular-nums",
                          align === "left" && "text-left",
                          align === "center" && "text-center",
                          align === "right" &&
                            "text-right font-mono tabular-nums",
                        )}
                      >
                        {col.cell(row)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          {tableFooter}
        </table>
      </div>
    </div>
  );
}
